import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { useState } from 'react'
import Link from 'next/link'
import {
    ChartColumn,
    ChevronRight,
    History,
    MonitorSmartphone,
    Settings,
    Tags
} from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { DataGate } from '@/components/app/data-gate'
import { PageContainer, PageHeader } from '@/components/app/page-header'
import { BusinessLogo } from '@/components/app/business-logo'
import { InstallSheet } from '@/components/app/install-sheet'
import { Card } from '@/components/kit/section'
import { useCarWashData } from '@/hooks/use-carwash'
import { useInstallPrompt } from '@/hooks/use-install-prompt'

type MenuItem = {
    href: string
    label: string
    description: string
    icon: LucideIcon
    iconClass: string
}

const MenuLink: FC<{ item: MenuItem }> = ({ item }): ReactNode => {
    const Icon = item.icon

    return (
        <Link
            href={item.href}
            className='flex items-center gap-3.5 px-4 py-3.5 transition-colors hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none'
        >
            <span className={`grid size-10 shrink-0 place-items-center rounded-xl ${item.iconClass}`}>
                <Icon className='size-5' aria-hidden='true' />
            </span>

            <span className='min-w-0 flex-1'>
                <span className='block text-[15px] font-semibold text-foreground'>
                    {item.label}
                </span>

                <span className='block truncate text-[13px] text-muted-foreground'>
                    {item.description}
                </span>
            </span>

            <ChevronRight className='size-5 shrink-0 text-slate-300' aria-hidden='true' />
        </Link>
    )
}

const MoreView: FC = (): ReactNode => {
    const { state } = useCarWashData()
    const { state: installState } = useInstallPrompt()
    const [installOpen, setInstallOpen] = useState(false)

    const activeServices = state.services.filter(service => service.active).length
    const knownVehicles = state.vehicles.filter(vehicle => vehicle.plate).length

    const items: MenuItem[] = [
        {
            href: '/more/services',
            label: 'Services & Pricing',
            description: `${activeServices} active ${activeServices === 1 ? 'service' : 'services'}`,
            icon: Tags,
            iconClass: 'bg-accent text-accent-foreground'
        },
        {
            href: '/more/customers',
            label: 'Customer History',
            description: `${knownVehicles} vehicles and their visits`,
            icon: History,
            iconClass: 'bg-violet-50 text-violet-700'
        },
        {
            href: '/more/reports',
            label: 'Reports',
            description: 'Revenue, vehicles, staff and popular services',
            icon: ChartColumn,
            iconClass: 'bg-emerald-50 text-emerald-700'
        },
        {
            href: '/more/settings',
            label: 'Business Settings',
            description: 'Name, logo, invoices, currency and WhatsApp messages',
            icon: Settings,
            iconClass: 'bg-slate-100 text-slate-700'
        }
    ]

    return (
        <>
            <PageHeader title='More' />

            <PageContainer className='max-w-3xl space-y-5'>
                <Link
                    href='/more/settings'
                    className='flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-card transition-shadow hover:shadow-raised focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
                >
                    <BusinessLogo logo={state.settings.logo} name={state.settings.name} className='size-14' />

                    <span className='min-w-0 flex-1'>
                        <span className='block truncate font-heading text-lg font-bold text-foreground'>
                            {state.settings.name}
                        </span>

                        <span className='block truncate text-[13px] text-muted-foreground'>
                            {state.settings.address || 'Add your address in settings'}
                        </span>
                    </span>

                    <ChevronRight className='size-5 shrink-0 text-slate-300' aria-hidden='true' />
                </Link>

                <Card className='divide-y divide-border overflow-hidden'>
                    {items.map(item => <MenuLink key={item.href} item={item} />)}
                </Card>

                {installState !== 'installed' && installState !== 'unknown' && (
                    <Card className='overflow-hidden'>
                        <button
                            type='button'
                            onClick={() => setInstallOpen(true)}
                            className='flex w-full items-center gap-3.5 px-4 py-3.5 text-left transition-colors hover:bg-slate-50 focus-visible:bg-slate-50 focus-visible:outline-none'
                        >
                            <span className='grid size-10 shrink-0 place-items-center rounded-xl bg-sky-50 text-sky-700'>
                                <MonitorSmartphone className='size-5' aria-hidden='true' />
                            </span>

                            <span className='min-w-0 flex-1'>
                                <span className='block text-[15px] font-semibold text-foreground'>
                                    Install app
                                </span>

                                <span className='block text-[13px] text-muted-foreground'>
                                    Add Washline to your home screen
                                </span>
                            </span>

                            <ChevronRight className='size-5 shrink-0 text-slate-300' aria-hidden='true' />
                        </button>
                    </Card>
                )}

                <p className='px-1 text-center text-xs leading-relaxed text-muted-foreground'>
                    Washline keeps your records on this device, so it works even without internet.
                </p>
            </PageContainer>

            <InstallSheet open={installOpen} onClose={() => setInstallOpen(false)} />
        </>
    )
}

const MorePage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title='More' description='Services and pricing, customer history, reports and business settings.' />

            <DataGate>
                <MoreView />
            </DataGate>
        </Layout>
    )
}

export default MorePage
